// src/hooks/useVersionCheck.ts
// December 24, 2025: Version check notification system
/**
 * .tuMetrics {"tu1": 0.93, "tu2": 0.95, "tu3": 5, "notes": "Stale-client + waiting SW; Refresh always hard-navigates; no ack-before-reload", "tuVersion": "2.5"}
 * .help "Compares this tab's baked buildTime to live version.json. Refresh clears caches and replaces the page; a waiting SW with the same build activates quietly."
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { APP_BUILD_META } from '@/utils/buildMeta';
import {
  decideStaleAction,
  decideUpdateBanner,
  recordReleaseVersionAck,
  type UpdateBannerMode,
  type VersionInfo,
} from './versionBanner';

export type { UpdateBannerMode, VersionInfo };

const DORMANCY_THRESHOLD_MS = 60_000;
const HEARTBEAT_MS = 20_000;

/** Running bundle identity — immutable for the lifetime of this page load. */
const RUNNING_BUILD_TIME = import.meta.env.DEV ? null : APP_BUILD_META.buildTime;
const RUNNING_PACKAGE_VERSION = import.meta.env.DEV ? null : APP_BUILD_META.packageVersion;

const useVersionCheck = (checkInterval: number = 60000) => {
  const [hasStaleDeploy, setHasStaleDeploy] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [bannerMode, setBannerMode] = useState<UpdateBannerMode>('refresh-only');
  const [isChecking, setIsChecking] = useState(false);
  const [latestVersion, setLatestVersion] = useState<VersionInfo | null>(null);

  const latestVersionRef = useRef<VersionInfo | null>(null);
  const bannerModeRef = useRef<UpdateBannerMode>('refresh-only');
  const hasStaleDeployRef = useRef(false);
  const lastHiddenAtRef = useRef<number>(Date.now());
  const lastTickRef = useRef<number>(Date.now());
  const waitingSW = useRef(false);
  const applyWaitingSW = useRef<((reloadPage?: boolean) => Promise<void>) | undefined>();

  const markStaleDeploy = useCallback((version: VersionInfo) => {
    const decided = decideUpdateBanner(version);

    console.log('🆕 Stale client — server deploy is newer than this tab', {
      runningBuildTime: RUNNING_BUILD_TIME,
      serverBuildTime: version.buildTime,
      commit: version.gitCommit,
      mode: decided.mode,
      waitingSW: waitingSW.current,
      versionLabel: version.releaseNotesVersion || version.packageVersion,
    });

    const payload: VersionInfo = {
      ...version,
      releaseSummary: decided.releaseSummary,
    };
    latestVersionRef.current = payload;
    bannerModeRef.current = decided.mode;
    setLatestVersion(payload);
    setBannerMode(decided.mode);
    hasStaleDeployRef.current = true;
    setHasStaleDeploy(true);
    setBannerDismissed(false);
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (isChecking) return;
    if (!RUNNING_BUILD_TIME && !waitingSW.current) return;

    setIsChecking(true);
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      const version: VersionInfo | null = response.ok ? await response.json() : null;
      const action = decideStaleAction({
        runningBuildTime: RUNNING_BUILD_TIME,
        serverBuildTime: version?.buildTime ?? null,
        runningPackageVersion: RUNNING_PACKAGE_VERSION,
        serverPackageVersion: version?.packageVersion ?? null,
        waitingSW: waitingSW.current,
      });

      if (action === 'activate-silent') {
        waitingSW.current = false;
        void applyWaitingSW.current?.(false);
        return;
      }
      if (action === 'banner' && version) {
        markStaleDeploy(version);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Version check failed:', error);
      }
    } finally {
      setIsChecking(false);
    }
  }, [isChecking, markStaleDeploy]);

  const reshowBannerIfStale = useCallback(() => {
    if (hasStaleDeployRef.current) {
      setBannerDismissed(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void import('virtual:pwa-register')
      .then(({ registerSW }) => {
        if (cancelled) return;
        applyWaitingSW.current = registerSW({
          immediate: true,
          onNeedRefresh() {
            waitingSW.current = true;
            void checkForUpdate();
          },
        });
      })
      .catch(() => {
        /* no service worker in this environment */
      });
    return () => {
      cancelled = true;
    };
  }, [checkForUpdate]);

  useEffect(() => {
    if (!RUNNING_BUILD_TIME) return;

    console.log('📦 Running bundle build time:', RUNNING_BUILD_TIME);

    const intervalId = setInterval(checkForUpdate, checkInterval);
    const initialCheckTimeout = setTimeout(checkForUpdate, 5000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAtRef.current = Date.now();
        return;
      }
      const dormantMs = Date.now() - lastHiddenAtRef.current;
      void checkForUpdate();
      if (dormantMs >= DORMANCY_THRESHOLD_MS) {
        console.log(`📦 Tab visible after ${Math.round(dormantMs / 1000)}s — re-show refresh if still stale`);
        reshowBannerIfStale();
      }
    };

    const handleWakeGap = (dormantMs: number, source: string) => {
      if (dormantMs < DORMANCY_THRESHOLD_MS) return;
      console.log(`📦 ${source} after ${Math.round(dormantMs / 1000)}s — checking for stale client`);
      void checkForUpdate();
      reshowBannerIfStale();
    };

    const handleFocus = () => {
      handleWakeGap(Date.now() - lastTickRef.current, 'Window focus');
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        handleWakeGap(Date.now() - lastTickRef.current, 'Page restored from bfcache');
      }
    };

    const heartbeatId = setInterval(() => {
      const now = Date.now();
      const gap = now - lastTickRef.current;
      if (gap >= DORMANCY_THRESHOLD_MS + HEARTBEAT_MS) {
        handleWakeGap(gap, 'Heartbeat sleep gap');
      }
      lastTickRef.current = now;
    }, HEARTBEAT_MS);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      clearInterval(intervalId);
      clearTimeout(initialCheckTimeout);
      clearInterval(heartbeatId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [checkInterval, checkForUpdate, reshowBannerIfStale]);

  const refreshApp = async (redirectTo?: string) => {
    // Do NOT ack here. Acking before navigation meant a stalled Refresh (waiting SW
    // hang, cache clear never finishing) demoted the next banner to "Update available"
    // with no version blurb while this tab was still on the old build. Dismiss acks.
    const base =
      redirectTo ||
      `${window.location.pathname}${window.location.search}${window.location.hash || ''}`;
    const sep = base.includes('?') ? '&' : '?';
    // Cache-bust so replace cannot reuse a bfcache / CDN-stale HTML document.
    const next = `${base}${sep}_refresh=${Date.now()}`;

    const cleanup = async () => {
      try {
        // vite-plugin-pwa's updateSW(true) only reloads when a worker is *waiting*.
        // Never await it unboundedly — a wedged waiting SW made Refresh a no-op.
        if (applyWaitingSW.current && waitingSW.current) {
          void applyWaitingSW.current(true);
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((name) => caches.delete(name)));
        }
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((reg) => reg.unregister()));
        }
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn('[useVersionCheck] refreshApp precache cleanup:', e);
        }
      }
    };

    await Promise.race([cleanup(), new Promise((r) => setTimeout(r, 2000))]);
    window.location.replace(next);
  };

  /** Hide banner temporarily — does NOT mark client as up to date (that was the bug). */
  const dismissUpdate = () => {
    setBannerDismissed(true);
    if (latestVersionRef.current && bannerModeRef.current === 'major') {
      recordReleaseVersionAck(latestVersionRef.current);
    }
    console.log('📦 Refresh banner dismissed — will reappear on wake if still stale');
  };

  const hasUpdate = hasStaleDeploy && !bannerDismissed;

  return { hasUpdate, latestVersion, refreshApp, dismissUpdate, bannerMode };
};

export default useVersionCheck;
